import React from 'react'
import styled from '@emotion/styled'
import {RoomOfferAccessFlowLabel} from 'components/Accommodation/Rooms/RoomOffer/RoomOfferAccessFlowLabel'
import {RoomsTopOfferPrice} from 'components/Accommodation/Rooms/SplitBookingCard/RoomsTopOfferPrice'
import {RoomSwitchRequiredTooltip} from 'components/Offer/Primary/RoomSwitchRequiredTooltip/RoomSwitchRequiredTooltip'

import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'
import {Offer} from '@vio/core/src/offer/types/offer'
import {
  getDisplayPrice,
  getTotalPrice
} from '@vio/core/src/price/business/price'
import {getSplitBookingDates} from '@vio/core/src/room/business/utils/getSplitBookingDates'
import {getSplitBookingRooms} from '@vio/core/src/room/business/utils/getSplitBookingRooms'
import {RoomCardEntityType} from '@vio/core/src/room/types/room'
import {Cashback} from '@vio/shared/src/cashback/components/Cashback'
import {useCashbackPositioning} from '@vio/shared/src/search/OfferLayout/useCashbackPositioning'
import {EarnPoints} from '@vio/shared/src/whiteLabel/EarnPoints'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'

import {PrimaryRegularOfferPrice} from './PrimaryRegularOfferPrice'
import {PrimaryRoomOfferCTA} from './PrimaryRoomOfferCTA'
import {PrimaryRoomOfferLabels} from './PrimaryRoomOfferLabels'
import {PrimaryRoomOfferLoading} from './PrimaryRoomOfferLoading'
import {PrimaryRoomOfferLogo} from './PrimaryRoomOfferLogo'
import {PrimaryRoomOfferTag} from './PrimaryRoomOfferTag'
import {usePrimaryRoomOfferData} from './usePrimaryRoomOfferData'

const RoomOfferWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${cssTheme.layout.spacing.s400};
`

interface Props {
  hotelId: string
  component: string
  showCashback?: boolean
}

export const PrimaryRoomOffer = ({hotelId, component, showCashback}: Props) => {
  const {
    ref,
    isRoomsPollingWaitingForResults,
    pickedEntity,
    pickedOffer,
    isSelected,
    isSplitBookingPicked,
    labelOffers,
    vioTagDetails,
    brandName,
    regularOfferId,
    flowType,
    shouldShowFreeCancellationDate,
    trackOfferClicked
  } = usePrimaryRoomOfferData({hotelId, component})

  const pickedOfferCashback = (pickedOffer as Offer)?.cashback
  const {canEarn: hasEarnPoints} = usePointsPriceDisplay()
  const {showCashbackOnLeft, showCashbackOnRight} = useCashbackPositioning({
    cashbackComponent:
      showCashback &&
      pickedOfferCashback?.value &&
      pickedOfferCashback?.type ? (
        <Cashback
          value={pickedOfferCashback.value}
          type={pickedOfferCashback.type}
          withInfoAction
        />
      ) : hasEarnPoints && pickedOffer ? (
        <EarnPoints
          amount={getTotalPrice(getDisplayPrice(pickedOffer.prices ?? []))}
        />
      ) : undefined
  })

  if (isRoomsPollingWaitingForResults) {
    return <PrimaryRoomOfferLoading />
  }
  if (!pickedOffer) return null

  return (
    <div ref={ref} data-id="BofhOffer">
      <Box paddingBottom="s200">
        <PrimaryRoomOfferTag
          isSelected={isSelected}
          vioTagDetails={vioTagDetails}
          brandName={brandName}
        />
      </Box>

      <RoomOfferWrapper>
        <div>
          <Box
            display="flex"
            flexDirection="column"
            gap="s200"
            alignItems="flex-start"
          >
            <PrimaryRoomOfferLogo />
            <PrimaryRoomOfferLabels
              shouldShowFreeCancellationDate={shouldShowFreeCancellationDate}
              offers={labelOffers}
            />
            {showCashbackOnLeft && <Box>{showCashbackOnLeft}</Box>}
          </Box>
          {isSplitBookingPicked ? (
            <RoomSwitchRequiredTooltip colorPath="content.neutral.secondary" />
          ) : null}
        </div>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          justifyContent="flex-end"
        >
          {pickedEntity.type === RoomCardEntityType.SplitBooking ? (
            <RoomsTopOfferPrice
              hotelId={hotelId}
              prices={pickedOffer.prices}
              splitBookingDates={getSplitBookingDates(pickedEntity.data)}
              splitBookingRooms={getSplitBookingRooms(pickedEntity.data)}
            />
          ) : (
            <>
              <RoomOfferAccessFlowLabel offerId={regularOfferId} />
              <PrimaryRegularOfferPrice
                hotelId={hotelId}
                room={pickedEntity.data}
                offer={pickedOffer as Offer}
                component={component}
                isVioTagVisible={vioTagDetails.isVioTagVisible}
              />
            </>
          )}

          {showCashbackOnRight && (
            <Box marginTop="s200" maxWidth="100%">
              {showCashbackOnRight}
            </Box>
          )}

          <PrimaryRoomOfferCTA
            trackOfferClicked={trackOfferClicked}
            flowType={flowType}
          />
        </Box>
      </RoomOfferWrapper>
    </div>
  )
}
